import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Search, FileText, Users, CalendarDays, BookOpen, UserCog, Receipt, Briefcase, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';

const SOURCES = [
  { key: 'sessions', path: '/sessions', route: '/app/sessions', icon: CalendarDays, label: 'Sessions', fields: ['id', 'title', 'trainer'] },
  { key: 'learners', path: '/learners', route: '/app/stagiaires', icon: Users, label: 'Apprenants', fields: ['firstName', 'lastName', 'email', 'company'] },
  { key: 'programs', path: '/programs', route: '/app/programmes', icon: BookOpen, label: 'Programmes', fields: ['title', 'category'] },
  { key: 'trainers', path: '/trainers', route: '/app/formateurs', icon: UserCog, label: 'Formateurs', fields: ['name', 'speciality'] },
  { key: 'quotes', path: '/quotes', route: '/app/devis', icon: FileText, label: 'Devis', fields: ['id', 'client'] },
  { key: 'invoices', path: '/invoices', route: '/app/factures', icon: Receipt, label: 'Factures', fields: ['id', 'client'] },
  { key: 'prospects', path: '/prospects', route: '/app/crm', icon: Briefcase, label: 'Prospects', fields: ['company', 'contact'] },
];

export default function GlobalSearch({ open, onOpenChange }) {
  const [query, setQuery] = useState('');
  const [cache, setCache] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    SOURCES.forEach((src) => {
      if (cache[src.key]) return;
      api.get(src.path).then(({ data }) => setCache((c) => ({ ...c, [src.key]: data }))).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const q = query.trim().toLowerCase();
  const results = q.length < 2 ? [] : SOURCES.flatMap((src) => {
    const data = cache[src.key] || [];
    return data
      .filter((item) => src.fields.some((f) => String(item[f] ?? '').toLowerCase().includes(q)))
      .slice(0, 4)
      .map((item) => ({ src, item }));
  }).slice(0, 12);

  const go = (src, item) => {
    onOpenChange(false);
    setQuery('');
    navigate(src.route);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[620px] p-0 rounded-2xl overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[#EFF0F6]">
          <Search className="w-[18px] h-[18px] text-[#8E8FA5]" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher partout (sessions, apprenants, devis…)"
            className="flex-1 h-9 text-[14px] outline-none placeholder:text-[#8E8FA5]"
          />
          <kbd className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded-md border border-[#E5E7EB] bg-white text-[#8E8FA5]">ESC</kbd>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {q.length < 2 && (
            <div className="p-8 text-center text-[13px] text-[#8E8FA5]">Tapez au moins 2 caractères pour lancer la recherche.</div>
          )}
          {q.length >= 2 && results.length === 0 && (
            <div className="p-8 text-center text-[13px] text-[#8E8FA5]">Aucun résultat pour "{query}"</div>
          )}
          {results.length > 0 && (
            <div className="py-2">
              {results.map(({ src, item }, i) => {
                const Icon = src.icon;
                const title = item.title || item.firstName || item.name || item.company || item.id;
                const subtitle = item.lastName || item.client || item.email || item.speciality || item.category || '';
                return (
                  <button
                    key={i}
                    onClick={() => go(src, item)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#FAFAFE] transition-colors text-left"
                  >
                    <div className="w-9 h-9 rounded-lg bg-[#EEF2FF] flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-[#4F46E5]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">
                        {item.firstName ? `${item.firstName} ${item.lastName}` : title}
                      </div>
                      <div className="text-[11.5px] text-[#8E8FA5]">{src.label} · {typeof subtitle === 'string' ? subtitle : ''}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#8E8FA5]" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
